<template>
  <div class="consulta-form">
    <h2 class="consulta-form__title">Agenda tu <em>valoración.</em></h2>
    <p class="consulta-form__subtitle">Déjanos tus datos y te contactamos en menos de 24 horas hábiles.</p>

    <form @submit.prevent="submit" novalidate>

      <div class="consulta-form__field">
        <label>NOMBRE COMPLETO</label>
        <input
          v-model="form.name"
          type="text"
          placeholder="Tu nombre"
          :class="{ 'is-invalid': v$.form.name.$error }"
          @blur="v$.form.name.$touch()"
        />
        <span v-if="v$.form.name.$error" class="consulta-form__error">El nombre es requerido</span>
      </div>

      <div class="consulta-form__row">
        <div class="consulta-form__field">
          <label>TELÉFONO</label>
          <input
            v-model="form.phone"
            type="tel"
            placeholder="+52 656 000 0000"
            :class="{ 'is-invalid': v$.form.phone.$error }"
            @blur="v$.form.phone.$touch()"
          />
          <span v-if="v$.form.phone.$error" class="consulta-form__error">El teléfono es requerido</span>
        </div>

        <div class="consulta-form__field">
          <label>EMAIL</label>
          <input
            v-model="form.email"
            type="email"
            placeholder="tu@correo.com"
            :class="{ 'is-invalid': v$.form.email.$error }"
            @blur="v$.form.email.$touch()"
          />
          <span v-if="v$.form.email.$error" class="consulta-form__error">
            {{ v$.form.email.required.$invalid ? 'El correo es requerido' : 'Correo inválido' }}
          </span>
        </div>
      </div>

      <div class="consulta-form__field">
        <label>SERVICIO DE INTERÉS</label>
        <select
          v-model="form.service"
          :class="{ 'is-invalid': v$.form.service.$error }"
          @blur="v$.form.service.$touch()"
        >
          <option value="" disabled>Selecciona un servicio</option>
          <option value="Cirugía refractiva (FemtoLASIK / PRK / ICL)">Cirugía refractiva (FemtoLASIK / PRK / ICL)</option>
          <option value="Catarata / LIO premium">Catarata / LIO premium</option>
          <option value="Queratocono / córnea">Queratocono / córnea</option>
          <option value="Presbicia">Presbicia</option>
          <option value="Estética ocular">Estética ocular</option>
          <option value="Valoración general">Valoración general</option>
        </select>
        <span v-if="v$.form.service.$error" class="consulta-form__error">Selecciona un servicio</span>
      </div>

      <div class="consulta-form__field">
        <label>MENSAJE (OPCIONAL)</label>
        <textarea
          v-model="form.message"
          rows="4"
          placeholder="Cuéntanos brevemente tu caso..."
        ></textarea>
      </div>

      <div v-if="serverError" class="consulta-form__server-error">{{ serverError }}</div>

      <div v-if="success" class="consulta-form__success">
        ¡Solicitud enviada! Nos pondremos en contacto pronto.
      </div>

      <button type="submit" :disabled="loading">
        <span v-if="loading">ENVIANDO...</span>
        <span v-else>ENVIAR SOLICITUD</span>
      </button>

      <p class="consulta-form__disclaimer">Tus datos se tratan con confidencialidad médica.</p>

    </form>
  </div>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import axios from 'axios'

export default defineComponent({
  name: 'ConsultaForm',

  setup() {
    const form = reactive({ name: '', phone: '', email: '', service: '', message: '' })
    const loading = ref(false)
    const success = ref(false)
    const serverError = ref('')

    const rules = {
      form: {
        name:    { required },
        phone:   { required },
        email:   { required, email },
        service: { required },
      }
    }

    const v$ = useVuelidate(rules, { form })

    async function submit() {
      serverError.value = ''
      success.value = false

      const valid = await v$.value.$validate()
      if (!valid) return

      loading.value = true
      try {
        const locale = document.documentElement.lang || 'es'
        await axios.post('/api/v1/pub/submissions', {
          data: {
            form_slug: 'contact-form',
            locale,
            name:    form.name,
            phone:   form.phone,
            email:   form.email,
            service: form.service,
            message: form.message
          }
        }, {
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-API-KEY': 'oO60HTCXroXwpsMSYP0cb0cg2hRYNtmSluF',
            'X-Contact-Form-Token': document.querySelector('meta[name="contact-form-token"]')?.content || ''
          }
        })

        success.value = true
        form.name = form.phone = form.email = form.service = form.message = ''
        v$.value.$reset()
      } catch (err) {
        const errors = err.response?.data?.errors
        serverError.value = errors?.join(', ') || 'Ocurrió un error. Intenta de nuevo.'
      } finally {
        loading.value = false
      }
    }

    return { form, v$, loading, success, serverError, submit }
  }
})
</script>

<style scoped lang="scss">
.consulta-form {
  max-width: 580px;
  width: 100%;

  &__title {
    font-size: 2rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 8px;

    em {
      font-style: italic;
      font-weight: 800;
    }
  }

  &__subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 24px;
    line-height: 1.5;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__field {
    margin-bottom: 16px;

    label {
      display: block;
      font-weight: 700;
      font-size: 0.72rem;
      color: rgba(255, 255, 255, 0.7);
      letter-spacing: 0.08em;
      margin-bottom: 6px;
    }

    input,
    textarea,
    select {
      width: 100%;
      background: #fff;
      border: none;
      border-radius: 4px;
      padding: 14px 16px;
      font-size: 1rem;
      color: #333;
      outline: none;
      box-sizing: border-box;
      resize: vertical;
      transition: box-shadow 0.2s;

      &:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.4); }
      &.is-invalid { box-shadow: 0 0 0 2px #ff6b6b; }
    }

    select {
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;
      cursor: pointer;
    }
  }

  &__error {
    display: block;
    font-size: 0.78rem;
    color: #ffb3b3;
    margin-top: 4px;
  }

  &__server-error {
    background: rgba(255,0,0,0.15);
    color: #ffb3b3;
    border-radius: 4px;
    padding: 10px 14px;
    font-size: 0.85rem;
    margin-bottom: 16px;
  }

  &__success {
    background: rgba(255,255,255,0.15);
    color: #fff;
    border-radius: 4px;
    padding: 12px 16px;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 16px;
    text-align: center;
  }

  &__disclaimer {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.6);
    margin-top: 12px;
    text-align: center;
  }

  button[type="submit"] {
    width: 100%;
    background: #6abf4b;
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 16px;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    cursor: pointer;
    transition: background 0.2s, opacity 0.2s;
    margin-top: 4px;

    &:hover:not(:disabled) { background: #58a83b; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
}
</style>
